import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  COMPLETENESS_SECTIONS,
  CompletenessLevel,
  ProfileCompleteness,
  completenessLevel,
  levelLabelKey,
  sectionLabelKey,
} from '../../../shared/model/profile-completeness';

/**
 * NP20-1192: Die Ampel "Profilvervollständigung" an der Berater-Kachel.
 *
 * Reine Anzeige: kein Klickziel, kein zweiter Fokuspunkt. Die Kachel selbst ist das
 * anklickbare Element (`role="button"`, `routerLink`) — der Tooltip laeuft deshalb ueber
 * `title` und nicht ueber `MatTooltip` an einem interaktiven Element.
 */
@Component({
  selector: 'app-completeness-meter',
  templateUrl: './completeness-meter.component.html',
  styleUrl: './completeness-meter.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
})
export class CompletenessMeterComponent {
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly source = signal<ProfileCompleteness | null>(null);
  /** Zaehlt bei jedem Sprachwechsel hoch, damit der zusammengesetzte Tooltip neu entsteht. */
  private readonly language = signal(0);

  constructor() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.language.update((count) => count + 1));
  }

  @Input({ required: true })
  set completeness(value: ProfileCompleteness | null | undefined) {
    this.source.set(value ?? null);
  }

  /** Kompakte Variante der Listenansicht: ohne Trennlinie, ohne oberen Abstand, kleinere Schrift. */
  @Input() compact = false;

  protected readonly badges = computed(() => this.source()?.totalBadges ?? 0);
  protected readonly percent = computed(() => this.source()?.percent ?? 0);
  protected readonly level = computed<CompletenessLevel>(() =>
    completenessLevel(this.badges()),
  );

  protected readonly badgeLabel = computed(() =>
    this.badges() === 1
      ? 'userlist.completeness.openBadge'
      : 'userlist.completeness.openBadges',
  );

  /**
   * Erste Zeile: Stufe, danach die Aufschluesselung `<Abschnitt>: <n>` — nur Abschnitte mit
   * mindestens einem Badge. Ohne Badges der feste Text "Vollständig · Keine roten Badges im
   * Profil".
   *
   * Zweite Zeile: die Stufenregel, damit am Zeiger steht, ab wie vielen Hinweisen welche
   * Farbe gilt. Dieselbe Angabe steht rechtsbuendig in der Filterleiste — dort sieht sie
   * nur, wer die Leiste im Blick hat.
   */
  protected readonly tooltip = computed(() => {
    this.language();
    const completeness = this.source();
    if (!completeness) return '';
    return `${this.summaryLine(completeness)}\n${this.translate.instant('userlist.completeness.rule')}`;
  });

  private summaryLine(completeness: ProfileCompleteness): string {
    if (this.badges() === 0) {
      return this.translate.instant('userlist.completeness.tooltipComplete');
    }
    const parts = COMPLETENESS_SECTIONS.filter(
      (section) => completeness.sections[section].badges > 0,
    ).map(
      (section) =>
        `${this.translate.instant(sectionLabelKey(section))}: ${completeness.sections[section].badges}`,
    );
    return [this.translate.instant(levelLabelKey(this.level())), ...parts].join(
      ' · ',
    );
  }

  protected readonly progressLabel = computed(() => {
    this.language();
    return this.translate.instant('userlist.completeness.progressAria', {
      percent: this.percent(),
    });
  });
}
